import {
  DeviceInfo,
  generateGenericId,
  getClientDeviceId,
  getDeviceOSInfo,
} from "src/utilities/device-info"

import { encodePostData } from "./common"
import { env, Platform, platform } from "./constants"
import { addBreadCrumb, captureSentryException } from "./sentry"

export interface AuthToken {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface AuthResponse {
  access_token: string
  refresh_token: string
  device_id: string
  expires_in: number
  token_type: "Bearer"
}

export interface AuthError {
  code: string
  message: string
}

type PasswordGrant = {
  grant_type: "password"
  username?: string
  password?: string
}

type RefreshTokenGrant = {
  grant_type: "refresh_token"
  refresh_token?: string
}

export type AuthGrant = PasswordGrant | RefreshTokenGrant
type AuthGrantWithDeviceInfo = AuthGrant & {
  device_info: string
  client_device_id?: string
}

export type PasswordCredentials = Pick<PasswordGrant, "username" | "password">

export interface PinResponse {
  interval: number
  device_code: string
  user_code: string
  expires_in: number
  verification_uri: string
  verification_uri_complete: string
}

export const getDeviceInfoStrings = async () => {
  let device_id: string | undefined
  let info: DeviceInfo
  let device_info: string | undefined
  try {
    info = await getDeviceOSInfo()
    device_info = `modelName: ${info?.modelName} / sdkVersion: ${info?.sdkVersion} / firmwareVersion: ${info?.firmwareVersion}`
  } catch (e) {
    console.log(e)
    captureSentryException(e)
  }
  try {
    device_id = await getClientDeviceId()
  } catch (e) {
    console.log(e)
    captureSentryException(e)
  }
  if (!device_id)
    device_id = "ERROR GENERATING NATIVE ID: " + generateGenericId()
  return { device_id, device_info: device_info ?? "unknown" }
}

/**
 * Fetches the auth token using an OAuth grant.
 * Currently only password and refresh_token grants are supported.
 *
 * @param grant The grant to use for authentication.
 * @returns The auth token
 */
const authRequest = async (grant: AuthGrant): Promise<AuthToken> => {
  const { device_id, device_info } = await getDeviceInfoStrings()
  const grantWithDeviceInfo: AuthGrantWithDeviceInfo = {
    ...grant,
    device_info: device_info ?? "",
    client_device_id: device_id,
  }
  const body = JSON.stringify(grantWithDeviceInfo)
  return fetch(`${env.backendUri}/oauth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
  })
    .then((response) => {
      if (response.status !== 200) {
        // Get the error message from the response body
        return response.json().then((error: AuthError) => {
          addBreadCrumb({
            data: {
              fetchRequest: "authRequest",
              endpoint: `${env.backendUri}/oauth/token`,
            },
          })
          throw new Error(error.message)
        })
      }
      return response.json()
    })
    .catch((e) => {
      addBreadCrumb({
        data: {
          fetchRequest: "authRequest",
          endpoint: `${env.backendUri}/oauth/token`,
        },
      })
      captureSentryException(e)
    })
    .then((response: AuthResponse) => {
      return {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        expiresIn: response.expires_in,
      }
    })
}

/**
 * Fetches the access token using OAuth password grant.
 * https://www.oauth.com/oauth2-servers/access-tokens/password-grant/
 *
 * @param AuthCredentials user credentails representing a login attempt
 * @returns a promise resolving into a token
 */
export const authRequestWithPassword = ({
  username,
  password,
}: PasswordCredentials): Promise<AuthToken> => {
  return authRequest({ grant_type: "password", username, password })
}

/**
 * Fetches the access token using OAuth refresh token grant.
 * https://www.oauth.com/oauth2-servers/access-tokens/refreshing-access-tokens/
 *
 * @param refreshToken refresh token to use for refreshing the access token
 * @returns a promise resolving into a token
 */
export const authRequestWithRefreshToken = (
  refreshToken: string,
): Promise<AuthToken> => {
  return authRequest({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  })
}

export const fetchReversePin = async (): Promise<PinResponse> => {
  const { device_id, device_info } = await getDeviceInfoStrings()
  const body = JSON.stringify({
    device_info,
    client_device_id: device_id,
    affiliate_group:
      platform === Platform.Tizen
        ? "samsung-smarttv"
        : platform === Platform.WebOS
          ? "lg-smarttv"
          : platform === Platform.Chromecast
            ? "chromecast-smarttv"
            : "web-smarttv",
  })
  return fetch(`${env.backendUri}/oauth/device`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
  })
    .then((response) => {
      if (response.status !== 200) {
        // Get the error message from the response body
        return response.json().then((error: AuthError) => {
          addBreadCrumb({
            data: {
              fetchRequest: "fetchPinRequest",
              endpoint: `${env.backendUri}/oauth/device`,
            },
          })
          throw new Error(error.message)
        })
      }
      return response.json()
    })
    .catch((e) => {
      addBreadCrumb({
        data: {
          fetchRequest: "fetchPinRequest",
          endpoint: `${env.backendUri}/oauth/device`,
        },
      })
      throw new Error(e.message)
    })
    .then((response: PinResponse) => response)
}

export const verifyReversePin = ({
  device_code,
}: {
  device_code: string
}): Promise<{
  response: {
    code?: string
    access_token?: string
    refresh_token?: string
    expires_in?: number
  }
  status: number
}> => {
  return fetch(`${env.backendUri}/oauth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body: encodePostData({ device_code, grant_type: "device" }),
  }).then(async (response) => {
    const res = await response.json()
    return { response: res, status: response.status }
  })
}
